import { AnyAction, Middleware, createAction } from '@reduxjs/toolkit'

// Types
export interface OptimisticUpdatePayload<T> {
  key: string
  data: T
  rollbackData?: T
}

export interface OptimisticState {
  pending: Record<string, any>
  rollbackData: Record<string, any>
}

// Action creators
export const optimisticUpdate = createAction<OptimisticUpdatePayload<any>>('optimistic/update')
export const completeOptimisticUpdate = createAction<string>('optimistic/complete')
export const rollbackOptimisticUpdate = createAction<string>('optimistic/rollback')
export const saveRollbackData = createAction<{ key: string; data: any }>('optimistic/saveRollbackData')

export interface OptimisticAction<T = any> {
  type: string
  payload: T
  meta?: {
    optimistic?: boolean
    rollback?: boolean
    originalAction?: any
  }
}

// Define store type to avoid circular reference
type Store = {
  getState: () => any
  dispatch: (action: AnyAction) => any
}

export const optimisticMiddleware: Middleware = (store: Store) => next => action => {
  if (optimisticUpdate.match(action)) {
    const { key, data, rollbackData } = action.payload
    // Store the previous state for potential rollback
    store.dispatch(saveRollbackData({ key, data: rollbackData }))

    // Update the pending state
    store.dispatch({
      type: 'optimistic/setPending',
      payload: { key, data }
    })
  }

  if (completeOptimisticUpdate.match(action)) {
    // Clean up rollback data and pending state
    store.dispatch({
      type: 'optimistic/clearPending',
      payload: action.payload // key
    })
  }

  if (rollbackOptimisticUpdate.match(action)) {
    const state = store.getState()
    const key = action.payload
    const rollbackData = state.optimistic?.rollbackData[key]

    if (rollbackData) {
      // Restore the previous state
      store.dispatch({
        type: 'optimistic/restore',
        payload: { key, data: rollbackData }
      })
    }

    // Clean up
    store.dispatch({
      type: 'optimistic/clearPending',
      payload: key
    })
  }

  return next(action)
}

function isOptimisticAction(action: unknown): action is OptimisticAction {
  return (
    typeof action === 'object' &&
    action !== null &&
    'type' in action &&
    'meta' in action &&
    typeof (action as any).meta === 'object' &&
    (action as any).meta?.optimistic === true
  )
}
